'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useEffect } from 'react'

interface HomeInstagramProps {
	heading: {
		title: string
	}
	social_links: {
		external_links_id: {
			title: string
			url: string
			label: string
			target: string
		}
	}[]
}

export const fields = [
	'heading.title',
	'social_links.external_links_id.title', 
	'social_links.external_links_id.url',
	'social_links.external_links_id.target',
]

// Custom hook to handle Instagram feed accessibility
const useInstagramFeedAccessibility = () => {
	useEffect(() => {
		const addAccessibilityToInstagramLinks = () => {
			// Find all Instagram feed links that open in new tabs
			const instagramLinks = document.querySelectorAll('.eapps-instagram-feed-posts-item-link[target="_blank"]')
			
			instagramLinks.forEach((link) => {
				const linkElement = link as HTMLAnchorElement
				
				// Only add aria-label if it doesn't already exist
				if (!linkElement.getAttribute('aria-label')) {
					// Create a shorter, more concise aria-label
					const ariaLabel = 'Instagram post (opens in new tab)'
					linkElement.setAttribute('aria-label', ariaLabel)
				}
			})
		}

		// Initial check
		addAccessibilityToInstagramLinks()

		// Set up a mutation observer to watch for dynamically added content
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
					// Check if any Instagram feed links were added
					const hasInstagramLinks = Array.from(mutation.addedNodes).some((node) => {
						if (node.nodeType === Node.ELEMENT_NODE) {
							const element = node as Element
							return element.classList?.contains('eapps-instagram-feed-posts-item-link') ||
								   element.querySelector?.('.eapps-instagram-feed-posts-item-link')
						}
						return false
					})

					if (hasInstagramLinks) {
						// Small delay to ensure the links are fully rendered
						setTimeout(addAccessibilityToInstagramLinks, 100)
					}
				}
			})
		})

		// Start observing the document for changes
		observer.observe(document.body, {
			childList: true,
			subtree: true
		})

		// Cleanup function
		return () => {
			observer.disconnect()
		}
	}, [])
}

export default function HomeInstagram({ data }: { data: HomeInstagramProps }) {
	// Use the custom hook
	useInstagramFeedAccessibility()

	return (
		<>
		<section className='HomeInstagram w-full py-8 md:py-24 overflow-hidden'>
			<div className='container'>
				{/* Header with social media */}
				<div className='flex flex-col lg:flex-row justify-between items-start mb-12 items-center'>
					<h2 className='mb-6 lg:mb-0'>{data?.heading?.title || ''}</h2>
					<div className='flex items-center gap-4 '>
						{data?.social_links?.map((link, index) => (
							<Link
								key={index}
								href={link?.external_links_id?.url || ''}
								target={link?.external_links_id?.target || '_blank'}
								rel={link?.external_links_id?.target === '_blank' ? 'noopener noreferrer' : undefined}
								aria-label={
									link?.external_links_id?.target === '_blank'
										? `${link?.external_links_id?.title} (opens in new tab)`
										: link?.external_links_id?.title
								}
								className='w-6 h-6 flex items-center justify-center'
							>
								{link?.external_links_id?.title === 'Instagram' && (
									<>
										<Image
											src='/images/instagram.svg'
											alt='Instagram Logo'
											width={24}
											height={24}
											className='w-5 h-5 md:hidden'
											priority
										/>
										<svg
											width='24'
											height='24'
											viewBox='0 0 24 24'
											className='hidden md:block stroke-black hover:stroke-[#E4405F] transition-colors'
											fill='none'
											xmlns='http://www.w3.org/2000/svg'
										>
											<path
												d='M11.563 16.7788C14.2022 16.7788 16.3417 14.6393 16.3417 12.0002C16.3417 9.36097 14.2022 7.22149 11.563 7.22149C8.92384 7.22149 6.78436 9.36097 6.78436 12.0002C6.78436 14.6393 8.92384 16.7788 11.563 16.7788Z'
												strokeWidth='1.792'
												strokeLinecap='round'
												strokeLinejoin='round'
											/>
											<path
												d='M16.4163 1.39733H6.70966C3.53439 1.39733 0.960323 3.9714 0.960323 7.14667V16.8533C0.960323 20.0286 3.53439 22.6027 6.70966 22.6027H16.4163C19.5916 22.6027 22.1657 20.0286 22.1657 16.8533V7.14667C22.1657 3.9714 19.5916 1.39733 16.4163 1.39733Z'
												strokeWidth='1.792'
												strokeLinecap='round'
												strokeLinejoin='round'
											/>
										</svg>
									</>
								)}
								{link?.external_links_id?.title === 'Facebook' && (
									<>
										<Image
											src='/images/facebook.svg'
											alt='Facebook Logo'
											width={24}
											height={24}
											className='w-5 h-5 md:hidden'
											priority
										/>
										<svg
											width='23'
											height='24'
											viewBox='0 0 23 24'
											className='hidden md:block w-full h-full fill-black hover:fill-[#1877F2] transition-colors'
											xmlns='http://www.w3.org/2000/svg'
										>
											<path d='M11.3763 0.799988C5.2014 0.799988 0.176331 5.82505 0.176331 12C0.176331 18.1749 5.2014 23.2 11.3763 23.2C17.5513 23.2 22.5763 18.1749 22.5763 12C22.5763 5.82505 17.5513 0.799988 11.3763 0.799988ZM11.3763 2.66665C16.5423 2.66665 20.7097 6.83399 20.7097 12C20.7114 14.2342 19.9099 16.3946 18.4516 18.0872C16.9932 19.7797 14.975 20.8918 12.7651 21.2204V14.7216H15.4233L15.8405 12.0215H12.7651V10.5468C12.7651 9.42679 13.1338 8.43092 14.1819 8.43092H15.8666V6.07519C15.5707 6.03505 14.9445 5.94825 13.761 5.94825C11.2895 5.94825 9.841 7.25305 9.841 10.2267V12.0215H7.30046V14.7216H9.841V21.1971C7.66143 20.8383 5.68042 19.7163 4.25191 18.0315C2.8234 16.3467 2.04047 14.2089 2.043 12C2.043 6.83399 6.21033 2.66665 11.3763 2.66665Z' />
										</svg>
									</>
								)}
								{link?.external_links_id?.title === 'YouTube' && (
									<>
										<Image
											src='/images/youtube.svg'
											alt='YouTube Logo'
											width={29}
											height={20}
											className='w-6 h-6 md:hidden'
											priority
										/>
										<svg
											width='29'
											height='20'
											viewBox='0 0 29 20'
											className='hidden md:block w-full h-full fill-black hover:fill-[#FF0000] transition-colors'
											xmlns='http://www.w3.org/2000/svg'
										>
											<path d='M28.8563 9.91635C28.8563 9.85062 28.8563 9.77593 28.8531 9.68929C28.85 9.4473 28.8436 9.17543 28.8373 8.88563C28.812 8.05209 28.7676 7.22155 28.698 6.4388C28.603 5.36028 28.4636 4.46102 28.2736 3.78582C28.0731 3.0812 27.6801 2.43857 27.1339 1.92216C26.5876 1.40575 25.9073 1.03365 25.1608 0.84305C24.2646 0.615994 22.5103 0.475578 20.0403 0.388938C18.8655 0.347111 17.6083 0.320223 16.3511 0.305285C15.911 0.29931 15.5025 0.296323 15.1351 0.293335H14.2041C13.8368 0.296323 13.4283 0.29931 12.9881 0.305285C11.731 0.320223 10.4738 0.347111 9.29897 0.388938C6.82897 0.478565 5.07147 0.618982 4.17847 0.84305C3.43177 1.03318 2.75116 1.40512 2.20483 1.9216C1.6585 2.43809 1.26564 3.08097 1.06564 3.78582C0.872471 4.46102 0.736305 5.36028 0.641305 6.4388C0.571638 7.22155 0.527304 8.05209 0.501971 8.88563C0.492471 9.17543 0.489304 9.4473 0.486138 9.68929C0.486138 9.77593 0.482971 9.85062 0.482971 9.91635V10.0837C0.482971 10.1494 0.482971 10.2241 0.486138 10.3107C0.489304 10.5527 0.495638 10.8246 0.501971 11.1144C0.527304 11.9479 0.571638 12.7785 0.641305 13.5612C0.736305 14.6397 0.875638 15.539 1.06564 16.2142C1.47097 17.6452 2.66164 18.7745 4.17847 19.1569C5.07147 19.384 6.82897 19.5244 9.29897 19.6111C10.4738 19.6529 11.731 19.6798 12.9881 19.6947C13.4283 19.7007 13.8368 19.7037 14.2041 19.7067H15.1351C15.5025 19.7037 15.911 19.7007 16.3511 19.6947C17.6083 19.6798 18.8655 19.6529 20.0403 19.6111C22.5103 19.5214 24.2678 19.381 25.1608 19.1569C26.6776 18.7745 27.8683 17.6482 28.2736 16.2142C28.4668 15.539 28.603 14.6397 28.698 13.5612C28.7676 12.7785 28.812 11.9479 28.8373 11.1144C28.8468 10.8246 28.85 10.5527 28.8531 10.3107C28.8531 10.2241 28.8563 10.1494 28.8563 10.0837V9.91635ZM26.5763 10.0717C26.5763 10.1344 26.5763 10.2032 26.5731 10.2838C26.57 10.5169 26.5636 10.7738 26.5573 11.0516C26.5351 11.8463 26.4908 12.641 26.4243 13.379C26.3388 14.341 26.2185 15.1297 26.0696 15.6585C25.8733 16.3486 25.297 16.8953 24.5686 17.0776C23.9036 17.2449 22.2285 17.3793 19.9516 17.46C18.799 17.5018 17.5576 17.5287 16.3195 17.5437C15.8856 17.5496 15.4835 17.5526 15.1225 17.5526H14.2168L13.0198 17.5437C11.7816 17.5287 10.5435 17.5018 9.38764 17.46C7.11081 17.3763 5.43247 17.2449 4.77064 17.0776C4.0423 16.8924 3.46597 16.3486 3.26964 15.6585C3.1208 15.1297 3.00047 14.341 2.91497 13.379C2.84847 12.641 2.8073 11.8463 2.78197 11.0516C2.77247 10.7738 2.7693 10.5139 2.76614 10.2838C2.76614 10.2032 2.76297 10.1315 2.76297 10.0717V9.9283C2.76297 9.86556 2.76297 9.79684 2.76614 9.71618C2.7693 9.48315 2.77564 9.22621 2.78197 8.94837C2.80414 8.15367 2.84847 7.35897 2.91497 6.62104C3.00047 5.65904 3.1208 4.87032 3.26964 4.34151C3.46597 3.65138 4.0423 3.10465 4.77064 2.92241C5.43564 2.75511 7.11081 2.62066 9.38764 2.54C10.5403 2.49817 11.7816 2.47128 13.0198 2.45635C13.4536 2.45037 13.8558 2.44738 14.2168 2.44738H15.1225L16.3195 2.45635C17.5576 2.47128 18.7958 2.49817 19.9516 2.54C22.2285 2.62365 23.9068 2.75511 24.5686 2.92241C25.297 3.10764 25.8733 3.65138 26.0696 4.34151C26.2185 4.87032 26.3388 5.65904 26.4243 6.62104C26.4908 7.35897 26.532 8.15367 26.5573 8.94837C26.5668 9.22621 26.57 9.48614 26.5731 9.71618C26.5731 9.79684 26.5763 9.86855 26.5763 9.9283V10.0717ZM11.8513 14.0034L19.198 9.97012L11.8513 5.99664V14.0034Z' />
										</svg>
									</>
								)}
							</Link>
						))}
					</div>
				</div>

				{/* Elfsight Instagram Feed Widget */}
				<div
					className='elfsight-app-06560aa0-c0d6-4444-83f0-d84b3b93a96d'
					data-elfsight-app-lazy
					role='region'
					aria-label='Instagram Feed - Recent posts from our social media'
				/>
			</div>
		</section>
		</>
	)
}